import React, {
  FC,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  ReactText,
  useRef,
  useState,
} from 'react';
import { DndProvider } from 'react-dnd';
import nextId from 'react-id-generator';
import { Undefinable } from '@symfa-inc/providence-types';
import { Switch, Table } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import classNames from 'classnames';
import { TableComponents } from 'rc-table/lib/interface';
import { useDidUpdateEffect } from '@core/utils/hooks';
import { tableCount } from '@models/constants';
import { Browse } from '@models/types';
import { ColumnSettings } from '@shared/modules/browse-page/components';
import {
  COMPONENTS,
  DEFAULT_PAGINATION_OPTIONS,
  RND_CONTEXT,
} from './models/constants';
import { CustomTableProps } from './models/interfaces';
import { TableLegend } from './components';

import './styles.scss';

export const CustomTable: FC<CustomTableProps> = ({
  columns: columnsProps = [],
  scroll = { y: 'auto' },
  dataSource,
  onRowClick,
  rowClassName,
  className,
  pagination,
  place: { active, title } = tableCount(),
  moveRow,
  hoverable = true,
  module,
  subModule,
  legendProps,
  canOpenInNewTab = false,
  ...props
}: PropsWithChildren<CustomTableProps>) => {
  const [columns, setColumns] = useState<ColumnsType<Browse>>(columnsProps);
  const [isOpenInNewTab, setOpenInNewTab] = useState<boolean>(false);

  const toggleOpenInNewTab = () =>
    setOpenInNewTab((prevState: boolean) => !prevState);

  const manager = useRef(RND_CONTEXT);
  const components: Undefinable<TableComponents<any>> = moveRow && COMPONENTS;
  const paginationProps: false | TablePaginationConfig =
    pagination !== false
      ? {
          ...DEFAULT_PAGINATION_OPTIONS,
          ...pagination,
          ...(active
            ? {
                showTotal: (total: number): ReactNode => (
                  <span className="prov-custom-table__count">
                    {`${title ? `${title} ` : ''}Count:`}
                    <span className="quantity">{total}</span>
                  </span>
                ),
              }
            : {}),
        }
      : false;

  useDidUpdateEffect(() => {
    if (!module) {
      setColumns(columnsProps);
    }
  }, [columnsProps]);

  const content = (
    <div className="prov-custom-table">
      <DndProvider manager={manager.current.dragDropManager!}>
        {(module || legendProps) && (
          <div className="prov-custom-table__addition-content">
            {legendProps && legendProps.items.length && (
              <TableLegend {...legendProps} />
            )}
            {module && (
              <div className="prov-custom-table__settings-block">
                {canOpenInNewTab && (
                  <div className="prov-custom-table__settings-block__open-in-new-tab">
                    <span className="prov-custom-table__settings-block__open-in-new-tab__text">
                      Open in new tab
                    </span>
                    <Switch
                      size="small"
                      onChange={toggleOpenInNewTab}
                      checked={isOpenInNewTab}
                    />
                  </div>
                )}

                <ColumnSettings
                  columns={columnsProps}
                  setColumns={setColumns}
                  module={module}
                  subModule={subModule}
                />
              </div>
            )}
          </div>
        )}

        <Table
          className="prov-custom-table__grid"
          columns={columns}
          dataSource={dataSource}
          rowKey={(record: any): ReactText =>
            record?.generatedId ||
            record?.id ||
            record?._id ||
            nextId('custom-table')
          }
          pagination={paginationProps}
          rowClassName={(record: any, index: number, indent: number): string =>
            classNames(
              index % 2 === 0 ? 'odd-row' : 'even-row',
              rowClassName &&
                (typeof rowClassName === 'string'
                  ? rowClassName
                  : rowClassName(record, index, indent)),
              { hoverable },
            )
          }
          scroll={scroll}
          onRow={(
            record: any,
            index?: number,
          ): HTMLAttributes<HTMLElement> => ({
            onClick: (e: any): void =>
              onRowClick && onRowClick(record, isOpenInNewTab, e),
            ...(moveRow && {
              index,
              moveRow,
              className: 'draggable-row',
            }),
          })}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          components={components}
          {...props}
        />
      </DndProvider>
    </div>
  );

  return className ? <div className={className}>{content}</div> : content;
};
